import { FC, useId, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Form } from 'antd';

import { Country } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { CountriesList } from '@/shared/dictionaries/countries';
import { Address } from '@/shared/lib/forms/form-wrapper.types';
import { Title } from '@/shared/uikit/Title';
import { Button } from '@/shared/uikitV2/Button';
import { Input } from '@/shared/uikitV2/Input';
import { Select } from '@/shared/uikitV2/Select';
import { Option } from '@/shared/uikitV2/Select/types';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { BtnClose } from '@/widgets/modals/components/BtnClose';

import { useModalsStore } from '../../modals.store';
import { EditAddressModalProps } from '../../types';

import { Actions, Body, Container, Fields, Header, StyledDrawer } from './styled';

const MAX_LENGTH = 100;

const DEFAULT_VALUES: Address = {
  country: undefined,
  zipCode: '',
  district: '',
  city: '',
  street: '',
  buildingNumber: '',
  apartment: '',
};

export const EditAddressModal: FC<EditAddressModalProps> = ({
  address,
  onConfirm,
  countryReadonly,
}) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'editAddress' });
  const modals = useModalsStore();
  const formId = useId();

  const countryOptions = useMemo<Option[]>(
    () =>
      CountriesList.map((country) => ({
        value: country.key ?? '',
        content: { label: country.label },
      })),
    [],
  );

  const { control, handleSubmit: formHandleSubmit } = useForm<Address>({
    defaultValues: address ?? DEFAULT_VALUES,
  });

  const onClose = () => modals.closeModal();
  const onSubmit = formHandleSubmit((data) => {
    onConfirm(data);
    modals.closeModal();
  });

  return (
    <StyledDrawer open={true} closable={false} footer={null} width={400} onClose={onClose}>
      <Container>
        <Header>
          <Title size={22} bold>
            {t('title')}
          </Title>
          <BtnClose onClick={onClose} />
        </Header>
        <Body className="uikitScrollbar">
          <Fields>
            <Form onFinish={onSubmit} id={formId} style={{ display: 'contents' }}>
              <Controller
                control={control}
                name="country"
                render={({ field, fieldState }) => (
                  <Select
                    multiple={false}
                    options={countryOptions}
                    label={t('fields.country')}
                    status={fieldState.error ? 'error' : 'default'}
                    errorText={fieldState.error?.message}
                    iconAfter={<Country code={field.value} icon />}
                    {...field}
                    value={field.value ?? ''}
                    disabled={countryReadonly}
                  />
                )}
              />
              <Controller
                control={control}
                name="zipCode"
                render={({ field, fieldState }) => (
                  <Input
                    errorText={fieldState.error?.message}
                    label={t('fields.zipCode')}
                    maxLength={MAX_LENGTH}
                    {...field}
                  />
                )}
              />
              <Controller
                control={control}
                name="district"
                render={({ field, fieldState }) => (
                  <Input
                    errorText={fieldState.error?.message}
                    label={t('fields.district')}
                    maxLength={MAX_LENGTH}
                    {...field}
                  />
                )}
              />
              <Controller
                control={control}
                name="city"
                render={({ field, fieldState }) => (
                  <Input
                    errorText={fieldState.error?.message}
                    label={t('fields.city')}
                    maxLength={MAX_LENGTH}
                    {...field}
                  />
                )}
              />
              <Controller
                control={control}
                name="street"
                render={({ field, fieldState }) => (
                  <Input
                    errorText={fieldState.error?.message}
                    label={t('fields.street')}
                    maxLength={MAX_LENGTH}
                    {...field}
                  />
                )}
              />
              <Controller
                control={control}
                name="buildingNumber"
                render={({ field, fieldState }) => (
                  <Input
                    errorText={fieldState.error?.message}
                    label={t('fields.buildingNumber')}
                    maxLength={MAX_LENGTH}
                    {...field}
                  />
                )}
              />
              <Controller
                control={control}
                name="apartment"
                render={({ field, fieldState }) => (
                  <Input
                    errorText={fieldState.error?.message}
                    label={t('fields.apartment')}
                    maxLength={MAX_LENGTH}
                    {...field}
                  />
                )}
              />
            </Form>
          </Fields>
        </Body>
        <Actions>
          <Button
            type="text"
            onClick={onClose}
            style={{ color: COLORS.systemSky200, fontWeight: 700 }}
          >
            {t('btnCancel')}
          </Button>
          <Button htmlType="submit" form={formId}>
            {t('btnConfirm')}
          </Button>
        </Actions>
      </Container>
    </StyledDrawer>
  );
};
